import React, { useState, useCallback } from 'react';
import {
  ClipboardList,
  Loader2,
  Calendar,
  Clock,
  AlertCircle,
  CheckCircle,
  Upload,
  FileText,
  Download,
  ChevronLeft,
  Send,
  AlertTriangle,
  Award,
  Scale,
  Paperclip,
  X,
  Maximize2,
  Image,
  FileSpreadsheet,
  File,
} from 'lucide-react';
import { useAssignments, useAssignmentDetail, useSubmitAssignment } from '../../hooks/useStudent';
import { uploadSubmissionFile } from '../../services/studentService';
import type { StudentAssignmentResponse, AttachmentFile } from '@/types/student.types';
import { format, formatDistanceToNow, isPast, differenceInHours } from 'date-fns';
import { fr } from 'date-fns/locale';
import toast from 'react-hot-toast';
import mammoth from 'mammoth';
import { createSafeHtml } from '@/utils/sanitize';
import * as XLSX from 'xlsx';

interface AssignmentsTabProps {
  moduleElementId: string;
  // Past year = read-only consultation; absent = current year
  academicYearId?: string;
  // Former group's course instance; absent = the current group
  groupeId?: string;
  // True for a past year or a former group: assignments open, but nothing can be submitted
  readOnly?: boolean;
}

// File preview state for expanded view
interface FilePreviewState {
  isExpanded: boolean;
  fileUrl: string | null;
  fileName: string | null;
  loading: boolean;
  error: string | null;
  wordContent: string | null;
  excelData: { sheets: { name: string; data: string[][] }[]; activeSheet: number } | null;
}

const AssignmentsTab: React.FC<AssignmentsTabProps> = ({
  moduleElementId,
  academicYearId,
  groupeId,
  readOnly = false,
}) => {
  const { data: assignments = [], isLoading, error } = useAssignments(moduleElementId, academicYearId, groupeId);
  const [selectedAssignmentId, setSelectedAssignmentId] = useState<string | null>(null);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
        Erreur lors du chargement des devoirs
      </div>
    );
  }

  if (assignments.length === 0) {
    return (
      <div className="text-center py-12">
        <ClipboardList className="mx-auto h-12 w-12 text-gray-400" />
        <h3 className="mt-4 text-lg font-medium text-gray-900">Aucun devoir</h3>
        <p className="mt-2 text-sm text-gray-500">
          Votre professeur n'a pas encore créé de devoir pour ce cours.
        </p>
      </div>
    );
  }

  // If an assignment is selected, show detail view (not modal)
  if (selectedAssignmentId) {
    return (
      <AssignmentDetailView
        assignmentId={selectedAssignmentId}
        academicYearId={academicYearId}
        readOnly={readOnly}
        onBack={() => setSelectedAssignmentId(null)}
      />
    );
  }

  // Separate upcoming and past assignments
  const upcomingAssignments = assignments.filter((a) => !isPast(new Date(a.dueDate)));
  const pastAssignments = assignments.filter((a) => isPast(new Date(a.dueDate)));

  return (
    <div className="space-y-6">
      <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
        <ClipboardList className="h-5 w-5 text-blue-600" />
        Devoirs
      </h2>

      {/* Upcoming Assignments */}
      {upcomingAssignments.length > 0 && (
        <div className="space-y-4">
          <h3 className="text-sm font-medium text-gray-700 uppercase tracking-wide">
            À rendre
          </h3>
          {upcomingAssignments.map((assignment) => (
            <AssignmentCard
              key={assignment.id}
              assignment={assignment}
              onViewDetails={() => setSelectedAssignmentId(assignment.id)}
            />
          ))}
        </div>
      )}

      {/* Past Assignments */}
      {pastAssignments.length > 0 && (
        <div className="space-y-4">
          <h3 className="text-sm font-medium text-gray-700 uppercase tracking-wide">
            Passés
          </h3>
          {pastAssignments.map((assignment) => (
            <AssignmentCard
              key={assignment.id}
              assignment={assignment}
              onViewDetails={() => setSelectedAssignmentId(assignment.id)}
            />
          ))}
        </div>
      )}
    </div>
  );
};

// Assignment Card Component
interface AssignmentCardProps {
  assignment: StudentAssignmentResponse;
  onViewDetails: () => void;
}

const AssignmentCard: React.FC<AssignmentCardProps> = ({ assignment, onViewDetails }) => {
  const dueDate = new Date(assignment.dueDate);
  const isOverdue = isPast(dueDate) && !assignment.hasSubmitted;
  const hoursUntilDue = differenceInHours(dueDate, new Date());
  const isUrgent = hoursUntilDue > 0 && hoursUntilDue <= 48;

  // Get status badge
  const getStatusBadge = () => {
    switch (assignment.submissionStatus) {
      case 'graded':
        return (
          <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
            <CheckCircle className="h-3.5 w-3.5" />
            Noté: {assignment.score}/{assignment.maxScore}
          </span>
        );
      case 'submitted':
        return (
          <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
            <CheckCircle className="h-3.5 w-3.5" />
            Soumis
          </span>
        );
      case 'late':
        return (
          <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-800">
            <Clock className="h-3.5 w-3.5" />
            Soumis en retard
          </span>
        );
      default:
        return isOverdue ? (
          <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
            <AlertCircle className="h-3.5 w-3.5" />
            {assignment.score !== null
              ? `Non rendu — ${assignment.score}/${assignment.maxScore}`
              : 'Non rendu'}
          </span>
        ) : (
          <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
            <Clock className="h-3.5 w-3.5" />
            À rendre
          </span>
        );
    }
  };

  // Get deadline color/urgency
  const getDeadlineStyle = () => {
    if (isPast(dueDate)) {
      return 'text-red-600 bg-red-50 border-red-200';
    }
    if (isUrgent) {
      return 'text-amber-700 bg-amber-50 border-amber-200';
    }
    return 'text-gray-600 bg-gray-50 border-gray-200';
  };

  return (
    <div
      className={`bg-white rounded-xl border p-4 hover:shadow-md transition-all cursor-pointer ${
        isOverdue ? 'border-red-300 bg-red-50/30' : isUrgent ? 'border-amber-300' : 'border-gray-200'
      }`}
      onClick={onViewDetails}
    >
      <div className="flex items-start justify-between">
        <div className="flex-1 min-w-0">
          <h4 className="font-semibold text-gray-900">{assignment.title}</h4>
          {assignment.description && (
            <p className="text-sm text-gray-500 mt-1 line-clamp-2">{assignment.description}</p>
          )}
        </div>
        {getStatusBadge()}
      </div>

      <div className="flex items-center gap-4 mt-3">
        {/* Deadline badge with strong visual emphasis */}
        <div className={`flex items-center gap-2 px-3 py-1.5 rounded-lg border ${getDeadlineStyle()}`}>
          <Calendar className="h-4 w-4" />
          <span className="font-medium">{format(dueDate, 'd MMM yyyy', { locale: fr })}</span>
          <span className="text-gray-400">|</span>
          <Clock className="h-4 w-4" />
          <span className="font-medium">{format(dueDate, 'HH:mm')}</span>
        </div>

        {/* Time remaining */}
        {!isPast(dueDate) && (
          <span className={`text-sm font-medium ${isUrgent ? 'text-amber-600' : 'text-gray-500'}`}>
            {isUrgent && <AlertTriangle className="inline h-4 w-4 mr-1" />}
            Dans {formatDistanceToNow(dueDate, { locale: fr })}
          </span>
        )}

        {/* Max score */}
        <span className="text-sm text-gray-500 flex items-center gap-1">
          <Award className="h-4 w-4" />
          {assignment.maxScore} pts
        </span>
      </div>
    </div>
  );
};

// API Base URL for file access
const API_BASE_URL = `${import.meta.env.VITE_API_BASE_URL || 'http://localhost:4000'}`;

// Helper to convert relative URLs to full URLs
const getFullUrl = (url: string | null | undefined): string => {
  if (!url) return '';
  // If it's already a full URL or data URL, return as-is
  if (url.startsWith('http') || url.startsWith('data:') || url.startsWith('blob:')) {
    return url;
  }
  // If it's a relative path (like /uploads/...), prefix with API base
  if (url.startsWith('/')) {
    return `${API_BASE_URL}${url}`;
  }
  return url;
};

// Assignment Detail View Component (not modal - inline page)
interface AssignmentDetailViewProps {
  assignmentId: string;
  academicYearId?: string;
  readOnly?: boolean;
  onBack: () => void;
}

const AssignmentDetailView: React.FC<AssignmentDetailViewProps> = ({
  assignmentId,
  academicYearId,
  readOnly = false,
  onBack,
}) => {
  const { data: assignment, isLoading, error, refetch } = useAssignmentDetail(assignmentId, academicYearId);
  const submitMutation = useSubmitAssignment();

  // Submission form state
  const [submissionTitle, setSubmissionTitle] = useState('');
  const [fileUrl, setFileUrl] = useState<string | null>('');
  const [fileName, setFileName] = useState<string | null>('');
  const [fileSize, setFileSize] = useState<number | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);

  // File preview state
  const [expandedPreview, setExpandedPreview] = useState<FilePreviewState>({
    isExpanded: false,
    fileUrl: null,
    fileName: null,
    loading: false,
    error: null,
    wordContent: null,
    excelData: null,
  });

  // Get file extension
  const getFileExtension = (name: string | null | undefined): string => {
    if (!name) return '';
    return name.split('.').pop()?.toLowerCase() || '';
  };

  // Check file types
  const isWordDocument = (name: string | null | undefined): boolean => {
    const ext = getFileExtension(name);
    return ['doc', 'docx'].includes(ext);
  };

  const isExcelDocument = (name: string | null | undefined): boolean => {
    const ext = getFileExtension(name);
    return ['xls', 'xlsx', 'csv'].includes(ext);
  };

  const isPdfDocument = (name: string | null | undefined): boolean => {
    return getFileExtension(name) === 'pdf';
  };

  const isImageFile = (name: string | null | undefined): boolean => {
    const ext = getFileExtension(name);
    return ['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg', 'bmp'].includes(ext);
  };

  // Convert base64 to ArrayBuffer
  const base64ToArrayBuffer = (base64: string): ArrayBuffer => {
    const base64Data = base64.includes(',') ? base64.split(',')[1] : base64;
    const binaryString = window.atob(base64Data);
    const bytes = new Uint8Array(binaryString.length);
    for (let i = 0; i < binaryString.length; i++) {
      bytes[i] = binaryString.charCodeAt(i);
    }
    return bytes.buffer;
  };

  // Fetch file as ArrayBuffer (works for both data: URLs and server URLs)
  const fetchAsArrayBuffer = async (url: string): Promise<ArrayBuffer> => {
    const fullUrl = getFullUrl(url);
    if (fullUrl.startsWith('data:')) {
      return base64ToArrayBuffer(fullUrl);
    } else {
      const response = await fetch(fullUrl);
      return response.arrayBuffer();
    }
  };

  // Load Word document
  const loadWordDocument = useCallback(async (url: string) => {
    try {
      const arrayBuffer = await fetchAsArrayBuffer(url);
      const result = await mammoth.convertToHtml({ arrayBuffer });
      setExpandedPreview(prev => ({
        ...prev,
        loading: false,
        wordContent: result.value,
        error: null,
      }));
    } catch (err) {
      console.error('Error loading Word document:', err);
      setExpandedPreview(prev => ({
        ...prev,
        loading: false,
        error: 'Impossible de charger le document Word.',
      }));
    }
  }, []);

  // Load Excel document
  const loadExcelDocument = useCallback(async (url: string) => {
    try {
      const arrayBuffer = await fetchAsArrayBuffer(url);
      const workbook = XLSX.read(arrayBuffer, { type: 'array' });

      const sheets = workbook.SheetNames.map((name) => {
        const worksheet = workbook.Sheets[name];
        const jsonData = XLSX.utils.sheet_to_json<string[]>(worksheet, { header: 1 });
        return { name, data: jsonData as string[][] };
      });

      setExpandedPreview(prev => ({
        ...prev,
        loading: false,
        excelData: { sheets, activeSheet: 0 },
        error: null,
      }));
    } catch (err) {
      console.error('Error loading Excel document:', err);
      setExpandedPreview(prev => ({
        ...prev,
        loading: false,
        error: 'Impossible de charger le fichier Excel.',
      }));
    }
  }, []);

  // Expand/preview a file inline
  const handleExpandFile = useCallback(async (url: string, name: string) => {
    // Convert to full URL for display
    const fullUrl = getFullUrl(url);

    setExpandedPreview({
      isExpanded: true,
      fileUrl: fullUrl, // Store the full URL for display
      fileName: name,
      loading: true,
      error: null,
      wordContent: null,
      excelData: null,
    });

    if (isWordDocument(name)) {
      await loadWordDocument(url);
    } else if (isExcelDocument(name)) {
      await loadExcelDocument(url);
    } else {
      // PDF, images don't need special loading
      setExpandedPreview(prev => ({ ...prev, loading: false }));
    }
  }, [loadWordDocument, loadExcelDocument]);

  // Close expanded preview
  const handleClosePreview = () => {
    setExpandedPreview({
      isExpanded: false,
      fileUrl: null,
      fileName: null,
      loading: false,
      error: null,
      wordContent: null,
      excelData: null,
    });
  };

  // Handle file download
  const handleDownload = async (url: string, name: string) => {
    try {
      // Convert to full URL if needed
      const fullUrl = getFullUrl(url);

      if (fullUrl.startsWith('data:')) {
        const response = await fetch(fullUrl);
        const blob = await response.blob();
        const blobUrl = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = blobUrl;
        link.download = name || 'document';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(blobUrl);
      } else {
        // For regular URLs (including server URLs)
        const response = await fetch(fullUrl);
        const blob = await response.blob();
        const blobUrl = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = blobUrl;
        link.download = name || 'document';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(blobUrl);
      }
      toast.success('Téléchargement démarré');
    } catch (error) {
      console.error('Download failed:', error);
      toast.error('Erreur lors du téléchargement');
    }
  };

  // Handle file upload for submission - uploads to server
  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      try {
        // Show uploading state
        setFileName(file.name);
        setFileSize(file.size);
        setFileUrl(null); // Clear while uploading

        toast.loading('Téléchargement en cours...', { id: 'upload' });

        // Upload to server
        const response = await uploadSubmissionFile(file);

        // Set the server URL (not blob URL)
        setFileUrl(response.file.url);
        toast.success('Fichier téléchargé', { id: 'upload' });
      } catch (error: any) {
        toast.error(error.message || 'Erreur lors du téléchargement', { id: 'upload' });
        setFileName(null);
        setFileSize(null);
        setFileUrl(null);
      }
    }
  };

  // Handle submission with confirmation
  const handleSubmit = async () => {
    if (!fileUrl) {
      toast.error('Veuillez sélectionner un fichier');
      return;
    }
    if (!submissionTitle.trim()) {
      toast.error('Veuillez entrer un titre pour votre travail');
      return;
    }

    setShowConfirmModal(true);
  };

  // Confirm and submit
  const confirmSubmit = async () => {
    setShowConfirmModal(false);
    setIsSubmitting(true);

    try {
      await submitMutation.mutateAsync({
        assignmentId,
        dto: {
          fileUrl: fileUrl || undefined,
          fileName: fileName || undefined,
          fileSize: fileSize ?? undefined,
          content: submissionTitle.trim(), // Store title in content field for now
        },
      });
      toast.success('Devoir soumis avec succès!');
      refetch();
    } catch (err: any) {
      toast.error(err.message || 'Erreur lors de la soumission');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Get file icon
  const getFileIcon = (name: string | null | undefined) => {
    const ext = getFileExtension(name);
    if (['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg', 'bmp'].includes(ext)) return Image;
    if (['xls', 'xlsx', 'csv'].includes(ext)) return FileSpreadsheet;
    if (['pdf'].includes(ext)) return FileText;
    return File;
  };

  // Get file color
  const getFileColor = (name: string | null | undefined) => {
    const ext = getFileExtension(name);
    if (['pdf'].includes(ext)) return 'text-red-600 bg-red-100';
    if (['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg', 'bmp'].includes(ext)) return 'text-pink-600 bg-pink-100';
    if (['xls', 'xlsx', 'csv'].includes(ext)) return 'text-green-600 bg-green-100';
    if (['doc', 'docx'].includes(ext)) return 'text-blue-600 bg-blue-100';
    if (['ppt', 'pptx'].includes(ext)) return 'text-orange-600 bg-orange-100';
    return 'text-gray-600 bg-gray-100';
  };

  // Format file size
  const formatFileSize = (bytes: number | null | undefined): string => {
    if (!bytes) return '';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
      </div>
    );
  }

  if (error || !assignment) {
    return (
      <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
        Erreur lors du chargement du devoir
      </div>
    );
  }

  const dueDate = new Date(assignment.dueDate);
  // A past year is history: the server refuses the write, so never offer it.
  // `isCurrentYear` is the server's verdict; `readOnly` the page's selection.
  const isFormerGroup = assignment.isFormerGroup === true;
  const isClosedYear = readOnly || assignment.isCurrentYear === false || isFormerGroup;
  const canSubmit = !isClosedYear && (!isPast(dueDate) || assignment.allowLateSubmission);
  const hasSubmitted = !!assignment.mySubmission;
  const hoursUntilDue = differenceInHours(dueDate, new Date());
  const isUrgent = hoursUntilDue > 0 && hoursUntilDue <= 48;

  // Normalize attachments - handle both { name, url } and { fileName, url } formats
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const normalizeAttachment = (file: any): AttachmentFile => ({
    name: file.name || file.fileName || 'Document',
    url: file.url || '',
    size: file.size || file.fileSize,
    type: file.type || file.fileType,
  });

  // Combine all attachments and normalize them
  const allAttachments: AttachmentFile[] = (assignment.attachments || []).map(normalizeAttachment);

  // Render expanded preview content inline (no header with download button - simpler)
  const renderExpandedPreviewInline = () => {
    if (!expandedPreview.isExpanded || !expandedPreview.fileUrl) return null;

    return (
      <div className="bg-gray-100 rounded-b-xl border-x-2 border-b-2 border-blue-300 overflow-hidden">
        {/* Compact header with just close button */}
        <div className="flex items-center justify-end p-2 bg-gray-50 border-b border-gray-200">
          <button
            onClick={handleClosePreview}
            className="flex items-center gap-1 px-3 py-1.5 text-sm text-gray-600 hover:bg-gray-200 rounded-lg transition-colors"
          >
            <X className="h-4 w-4" />
            Fermer
          </button>
        </div>

        {/* Preview Content */}
        <div className="p-4 min-h-[300px] max-h-[500px] overflow-auto">
          {expandedPreview.loading && (
            <div className="flex flex-col items-center justify-center h-64">
              <Loader2 className="h-10 w-10 animate-spin text-blue-600 mb-4" />
              <p className="text-gray-600">Chargement du document...</p>
            </div>
          )}

          {expandedPreview.error && (
            <div className="flex flex-col items-center justify-center h-64 text-gray-500">
              <AlertCircle className="h-16 w-16 mb-4 text-amber-500" />
              <p className="text-lg font-medium">Erreur de chargement</p>
              <p className="text-sm mt-1">{expandedPreview.error}</p>
              <button
                onClick={() => handleDownload(expandedPreview.fileUrl!, expandedPreview.fileName || 'document')}
                className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              >
                Télécharger pour visualiser
              </button>
            </div>
          )}

          {/* PDF Preview */}
          {isPdfDocument(expandedPreview.fileName) && !expandedPreview.loading && !expandedPreview.error && (
            <iframe
              src={expandedPreview.fileUrl}
              className="w-full h-[450px] rounded-lg border border-gray-200 bg-white"
              title={expandedPreview.fileName || 'PDF'}
            />
          )}

          {/* Image Preview */}
          {isImageFile(expandedPreview.fileName) && !expandedPreview.loading && !expandedPreview.error && (
            <div className="flex items-center justify-center bg-white p-4 rounded-lg">
              <img
                src={expandedPreview.fileUrl}
                alt={expandedPreview.fileName || 'Image'}
                className="max-w-full max-h-[400px] object-contain rounded-lg shadow-lg"
              />
            </div>
          )}

          {/* Word Preview */}
          {isWordDocument(expandedPreview.fileName) && expandedPreview.wordContent && !expandedPreview.loading && (
            <div className="bg-white p-8 rounded-lg shadow-sm">
              <div
                className="prose prose-sm max-w-none"
                dangerouslySetInnerHTML={createSafeHtml(expandedPreview.wordContent)}
              />
            </div>
          )}

          {/* Excel Preview */}
          {isExcelDocument(expandedPreview.fileName) && expandedPreview.excelData && !expandedPreview.loading && (
            <div className="bg-white rounded-lg shadow-sm overflow-hidden">
              {/* Sheet tabs */}
              {expandedPreview.excelData.sheets.length > 1 && (
                <div className="flex gap-1 p-2 bg-gray-200 border-b overflow-x-auto">
                  {expandedPreview.excelData.sheets.map((sheet, index) => (
                    <button
                      key={sheet.name}
                      onClick={() => setExpandedPreview(prev => ({
                        ...prev,
                        excelData: prev.excelData ? { ...prev.excelData, activeSheet: index } : null
                      }))}
                      className={`px-3 py-1.5 text-sm rounded-t-lg whitespace-nowrap transition-colors ${
                        index === expandedPreview.excelData!.activeSheet
                          ? 'bg-white text-green-700 font-medium'
                          : 'bg-gray-100 text-gray-600 hover:bg-gray-50'
                      }`}
                    >
                      {sheet.name}
                    </button>
                  ))}
                </div>
              )}

              {/* Table */}
              <div className="overflow-auto max-h-[400px]">
                <table className="border-collapse min-w-full">
                  <tbody>
                    {expandedPreview.excelData.sheets[expandedPreview.excelData.activeSheet].data.map((row, rowIndex) => (
                      <tr key={rowIndex} className={rowIndex === 0 ? 'bg-green-50 font-medium sticky top-0' : 'hover:bg-gray-50'}>
                        <td className="px-2 py-1 text-xs text-gray-400 bg-gray-100 border border-gray-200 text-center min-w-[40px] sticky left-0">
                          {rowIndex + 1}
                        </td>
                        {row.map((cell, cellIndex) => (
                          <td
                            key={cellIndex}
                            className={`px-3 py-2 border border-gray-200 text-sm ${
                              rowIndex === 0 ? 'bg-green-50 font-medium text-green-900' : 'text-gray-700'
                            }`}
                          >
                            {cell !== undefined && cell !== null ? String(cell) : ''}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-6">
      {/* Back Button */}
      <button
        onClick={onBack}
        className="flex items-center gap-2 text-blue-600 hover:text-blue-700 font-medium"
      >
        <ChevronLeft className="h-5 w-5" />
        Retour aux devoirs
      </button>

      {/* Assignment Header */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6">
        <h2 className="text-2xl font-bold text-gray-900 mb-4">{assignment.title}</h2>

        {/* Deadline - prominent display */}
        <div className={`flex items-center gap-4 p-4 rounded-xl mb-4 ${
          isPast(dueDate)
            ? 'bg-red-100 border-2 border-red-300'
            : isUrgent
              ? 'bg-amber-100 border-2 border-amber-300'
              : 'bg-blue-50 border border-blue-200'
        }`}>
          <div className={`p-3 rounded-full ${
            isPast(dueDate) ? 'bg-red-200' : isUrgent ? 'bg-amber-200' : 'bg-blue-100'
          }`}>
            <Calendar className={`h-6 w-6 ${
              isPast(dueDate) ? 'text-red-600' : isUrgent ? 'text-amber-600' : 'text-blue-600'
            }`} />
          </div>
          <div className="flex-1">
            <p className={`text-sm font-medium ${
              isPast(dueDate) ? 'text-red-600' : isUrgent ? 'text-amber-600' : 'text-blue-600'
            }`}>
              Date limite
            </p>
            <p className={`text-xl font-bold ${
              isPast(dueDate) ? 'text-red-700' : isUrgent ? 'text-amber-700' : 'text-gray-900'
            }`}>
              {format(dueDate, "EEEE d MMMM yyyy 'à' HH:mm", { locale: fr })}
            </p>
          </div>
          <div className="text-right">
            {isPast(dueDate) ? (
              <span className="inline-flex items-center gap-1 px-3 py-1.5 bg-red-200 text-red-800 rounded-full text-sm font-medium">
                <AlertCircle className="h-4 w-4" />
                Date dépassée
              </span>
            ) : (
              <span className={`inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-sm font-medium ${
                isUrgent ? 'bg-amber-200 text-amber-800' : 'bg-blue-100 text-blue-700'
              }`}>
                <Clock className="h-4 w-4" />
                {formatDistanceToNow(dueDate, { locale: fr, addSuffix: true })}
              </span>
            )}
          </div>
        </div>

        {/* Score info */}
        <div className="flex items-center gap-4 text-sm text-gray-600">
          <div className="flex items-center gap-2">
            <Award className="h-5 w-5 text-blue-600" />
            <span>Note sur <strong>{assignment.maxScore}</strong> points</span>
          </div>
          {assignment.allowLateSubmission && assignment.latePenaltyPercent && (
            <div className="flex items-center gap-2 text-amber-600">
              <Clock className="h-5 w-5" />
              <span>Retards acceptés: {assignment.lateSubmissionDays}j (-{assignment.latePenaltyPercent}%/jour)</span>
            </div>
          )}
        </div>
      </div>

      {/* Instructions */}
      {assignment.instructions && (
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6">
          <div className="flex items-center gap-2 mb-4">
            <FileText className="h-5 w-5 text-blue-600" />
            <h3 className="font-semibold text-gray-900 text-lg">Instructions</h3>
          </div>
          <div
            className="prose prose-sm max-w-none text-gray-700"
            dangerouslySetInnerHTML={createSafeHtml(assignment.instructions)}
          />
        </div>
      )}

      {/* Attachments / Documents joints */}
      {allAttachments.length > 0 && (
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6">
          <div className="flex items-center gap-2 mb-4">
            <Paperclip className="h-5 w-5 text-green-600" />
            <h3 className="font-semibold text-gray-900 text-lg">Documents joints</h3>
            <span className="text-sm text-gray-400">({allAttachments.length} fichier{allAttachments.length > 1 ? 's' : ''})</span>
          </div>
          <div className="space-y-3">
            {allAttachments.map((file, index) => {
              const Icon = getFileIcon(file.name);
              const colorClass = getFileColor(file.name);
              const ext = getFileExtension(file.name);
              // Compare full URLs to handle relative paths correctly
              const isCurrentlyExpanded = expandedPreview.isExpanded && expandedPreview.fileUrl === getFullUrl(file.url);

              return (
                <div key={index}>
                  <div
                    className={`flex items-center gap-3 p-4 rounded-xl transition-colors ${
                      isCurrentlyExpanded
                        ? 'bg-blue-50 border-2 border-blue-300'
                        : 'bg-gray-50 hover:bg-gray-100'
                    }`}
                  >
                    <div className={`p-3 rounded-lg ${colorClass}`}>
                      <Icon className="h-6 w-6" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-gray-900">{file.name || 'Document sans nom'}</p>
                      <p className="text-xs text-gray-500">
                        {ext ? ext.toUpperCase() + ' Document' : 'Fichier'}
                        {file.size ? ` • ${formatFileSize(file.size)}` : ''}
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      {!isCurrentlyExpanded && (
                        <button
                          onClick={() => handleExpandFile(file.url, file.name)}
                          className="flex items-center gap-2 px-4 py-2 text-sm text-blue-600 bg-blue-50 hover:bg-blue-100 rounded-lg transition-colors"
                        >
                          <Maximize2 className="h-4 w-4" />
                          Voir
                        </button>
                      )}
                      <button
                        onClick={() => handleDownload(file.url, file.name)}
                        className="flex items-center gap-2 px-4 py-2 text-sm text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
                      >
                        <Download className="h-4 w-4" />
                        Télécharger
                      </button>
                    </div>
                  </div>

                  {/* Inline expanded preview for this specific file */}
                  {isCurrentlyExpanded && renderExpandedPreviewInline()}
                </div>
              );
            })}
          </div>

        </div>
      )}

      {/* Rubric / Grille d'évaluation */}
      {(assignment.rubricUrl || assignment.rubricFileName || assignment.rubricContent) && (
        <div className="bg-white rounded-xl border-2 border-purple-200 shadow-sm p-6">
          <div className="flex items-center gap-2 mb-4">
            <Scale className="h-5 w-5 text-purple-600" />
            <h3 className="font-semibold text-gray-900 text-lg">Grille d'évaluation</h3>
          </div>

          {/* Rubric content (rich text / table) - styled properly */}
          {assignment.rubricContent && (
            <div className="mb-4 overflow-x-auto">
              <style>{`
                .rubric-content {
                  font-size: 0.9rem;
                  line-height: 1.5;
                }
                .rubric-content table {
                  border-collapse: collapse;
                  width: 100%;
                  margin: 12px 0;
                  border-radius: 12px;
                  overflow: hidden;
                  border: 2px solid #e9d5ff;
                  box-shadow: 0 2px 8px rgba(147, 51, 234, 0.1);
                }
                .rubric-content th, .rubric-content td {
                  border: 1px solid #e9d5ff;
                  padding: 14px 18px;
                  text-align: left;
                  vertical-align: top;
                }
                .rubric-content th {
                  background: linear-gradient(135deg, #faf5ff 0%, #f3e8ff 100%);
                  font-weight: 700;
                  color: #7c3aed;
                  font-size: 0.875rem;
                  text-transform: uppercase;
                  letter-spacing: 0.05em;
                }
                .rubric-content tbody tr {
                  background-color: white;
                }
                .rubric-content tbody tr:nth-child(even) {
                  background-color: #faf5ff;
                }
                .rubric-content tbody tr:hover {
                  background-color: #f3e8ff;
                }
                .rubric-content td {
                  color: #374151;
                }
                .rubric-content p {
                  margin: 8px 0;
                }
              `}</style>
              <div
                className="rubric-content"
                dangerouslySetInnerHTML={createSafeHtml(assignment.rubricContent)}
              />
            </div>
          )}

          {/* Rubric file attachment */}
          {assignment.rubricUrl && assignment.rubricFileName && (() => {
            const rubricIcon = getFileIcon(assignment.rubricFileName);
            const rubricColorClass = getFileColor(assignment.rubricFileName);
            const rubricExt = getFileExtension(assignment.rubricFileName);
            // Compare full URLs to handle relative paths correctly
            const isRubricExpanded = expandedPreview.isExpanded && expandedPreview.fileUrl === getFullUrl(assignment.rubricUrl);

            return (
              <div>
                <div className={`flex items-center gap-3 p-4 rounded-xl transition-colors ${
                  isRubricExpanded
                    ? 'bg-purple-100 border-2 border-purple-400'
                    : 'bg-purple-50 hover:bg-purple-100'
                }`}>
                  <div className={`p-3 rounded-lg ${rubricColorClass}`}>
                    {React.createElement(rubricIcon, { className: 'h-6 w-6' })}
                  </div>
                  <div className="flex-1">
                    <p className="font-medium text-gray-900">{assignment.rubricFileName}</p>
                    <p className="text-xs text-gray-500">
                      {rubricExt ? rubricExt.toUpperCase() + ' Document' : 'Document de grille d\'évaluation'}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    {!isRubricExpanded && (
                      <button
                        onClick={() => handleExpandFile(assignment.rubricUrl!, assignment.rubricFileName!)}
                        className="flex items-center gap-2 px-4 py-2 text-sm text-purple-600 bg-purple-100 hover:bg-purple-200 rounded-lg transition-colors"
                      >
                        <Maximize2 className="h-4 w-4" />
                        Voir
                      </button>
                    )}
                    <button
                      onClick={() => handleDownload(assignment.rubricUrl!, assignment.rubricFileName!)}
                      className="flex items-center gap-2 px-4 py-2 text-sm text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
                    >
                      <Download className="h-4 w-4" />
                      Télécharger
                    </button>
                  </div>
                </div>

                {/* Inline expanded preview for rubric */}
                {isRubricExpanded && renderExpandedPreviewInline()}
              </div>
            );
          })()}
        </div>
      )}

      {/* Submission Section */}
      {hasSubmitted ? (
        // Show submitted work in read-only mode
        <div className="bg-white rounded-xl border-2 border-green-200 shadow-sm p-6">
          <div className="flex items-center gap-2 mb-4">
            <CheckCircle className="h-5 w-5 text-green-600" />
            <h3 className="font-semibold text-gray-900 text-lg">Mon travail soumis</h3>
            <span className="ml-auto inline-flex items-center gap-1 px-3 py-1 bg-green-100 text-green-700 rounded-full text-sm font-medium">
              <CheckCircle className="h-4 w-4" />
              Soumis
            </span>
          </div>

          {/* Submission info */}
          <div className="bg-green-50 rounded-xl p-4 mb-4">
            <div className="flex items-center gap-4 text-sm">
              <span className="text-green-700">
                Soumis le {format(new Date(assignment.mySubmission!.submittedAt), "d MMMM yyyy 'à' HH:mm", { locale: fr })}
              </span>
              {assignment.mySubmission!.isLate && (
                <span className="text-amber-600">
                  ({assignment.mySubmission!.lateDays} jour(s) de retard)
                </span>
              )}
            </div>
            {assignment.mySubmission!.content && (
              <p className="text-gray-700 mt-2">
                <strong>Titre:</strong> {assignment.mySubmission!.content}
              </p>
            )}
          </div>

          {/* Submitted file */}
          {assignment.mySubmission!.fileUrl && assignment.mySubmission!.fileName && (() => {
            const isSubmissionExpanded = expandedPreview.isExpanded && expandedPreview.fileUrl === getFullUrl(assignment.mySubmission!.fileUrl);
            return (
              <div>
                <div className={`flex items-center gap-3 p-4 rounded-xl transition-colors ${
                  isSubmissionExpanded
                    ? 'bg-green-100 border-2 border-green-400'
                    : 'bg-gray-50 hover:bg-gray-100'
                }`}>
                  <div className={`p-3 rounded-lg ${getFileColor(assignment.mySubmission!.fileName)}`}>
                    {React.createElement(getFileIcon(assignment.mySubmission!.fileName), { className: 'h-6 w-6' })}
                  </div>
                  <div className="flex-1">
                    <p className="font-medium text-gray-900">{assignment.mySubmission!.fileName}</p>
                    {assignment.mySubmission!.fileSize && (
                      <p className="text-xs text-gray-500">{formatFileSize(assignment.mySubmission!.fileSize)}</p>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    {!isSubmissionExpanded && (
                      <button
                        onClick={() => handleExpandFile(assignment.mySubmission!.fileUrl!, assignment.mySubmission!.fileName!)}
                        className="flex items-center gap-2 px-4 py-2 text-sm text-blue-600 bg-blue-50 hover:bg-blue-100 rounded-lg transition-colors"
                      >
                        <Maximize2 className="h-4 w-4" />
                        Voir
                      </button>
                    )}
                    <button
                      onClick={() => handleDownload(assignment.mySubmission!.fileUrl!, assignment.mySubmission!.fileName!)}
                      className="flex items-center gap-2 px-4 py-2 text-sm text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
                    >
                      <Download className="h-4 w-4" />
                      Télécharger
                    </button>
                  </div>
                </div>
                {/* Inline expanded preview for submitted file */}
                {isSubmissionExpanded && renderExpandedPreviewInline()}
              </div>
            );
          })()}

          {/* Grade if available */}
          {assignment.mySubmission!.status === 'GRADED' && (
            <div className="mt-4 p-4 bg-blue-50 rounded-xl border border-blue-200">
              <div className="flex items-center gap-4">
                <div>
                  <p className="text-sm text-blue-600 font-medium">Votre note</p>
                  <p className="text-3xl font-bold text-blue-900">
                    {assignment.mySubmission!.score}
                    <span className="text-lg text-blue-400">/{assignment.maxScore}</span>
                  </p>
                </div>
                {assignment.mySubmission!.feedback && (
                  <div className="flex-1 border-l border-blue-200 pl-4">
                    <p className="text-sm text-blue-600 font-medium">Commentaire du professeur</p>
                    <p className="text-gray-700 mt-1">{assignment.mySubmission!.feedback}</p>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      ) : isClosedYear ? (
        // Past academic year: consultation only
        <div className="bg-amber-50 border-2 border-amber-200 rounded-xl p-6 text-center">
          <AlertCircle className="h-12 w-12 text-amber-500 mx-auto mb-3" />
          <h3 className="text-lg font-semibold text-amber-800 mb-2">
            {isFormerGroup ? 'Devoir de votre ancien groupe' : 'Année académique clôturée'}
          </h3>
          <p className="text-amber-700">
            {isFormerGroup
              ? 'Ce devoir appartient à un groupe que vous avez quitté : il est consultable en lecture seule.'
              : 'Ce devoir est consultable en lecture seule et ne peut plus être rendu.'}
          </p>
        </div>
      ) : canSubmit ? (
        // Submission form
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6">
          <div className="flex items-center gap-2 mb-4">
            <Upload className="h-5 w-5 text-blue-600" />
            <h3 className="font-semibold text-gray-900 text-lg">Soumettre mon travail</h3>
          </div>

          {/* Late warning */}
          {isPast(dueDate) && assignment.allowLateSubmission && (
            <div className="mb-4 p-4 bg-amber-50 border border-amber-200 rounded-xl">
              <div className="flex items-center gap-2 text-amber-700">
                <AlertTriangle className="h-5 w-5" />
                <p className="font-medium">Attention: Votre soumission sera marquée comme en retard.</p>
              </div>
              {assignment.latePenaltyPercent && (
                <p className="text-sm text-amber-600 mt-1">
                  Une pénalité de {assignment.latePenaltyPercent}% par jour de retard sera appliquée.
                </p>
              )}
            </div>
          )}

          {/* Title field (required) */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Titre de votre travail <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={submissionTitle}
              onChange={(e) => setSubmissionTitle(e.target.value)}
              placeholder="Ex: Rapport d'analyse - Marketing Digital"
              className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          {/* File Upload */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Fichier à rendre <span className="text-red-500">*</span>
            </label>
            <div className="border-2 border-dashed border-gray-300 rounded-xl p-8 text-center hover:border-blue-400 transition-colors">
              <input
                type="file"
                onChange={handleFileChange}
                className="hidden"
                id="file-upload"
                accept={assignment.acceptedFileTypes?.map(t => `.${t.toLowerCase()}`).join(',') || '*'}
              />
              <label htmlFor="file-upload" className="cursor-pointer">
                <Upload className="h-12 w-12 text-gray-400 mx-auto mb-3" />
                {fileName ? (
                  <div className="space-y-2">
                    <p className="text-lg font-medium text-blue-600">{fileName}</p>
                    {fileSize && (
                      <p className="text-sm text-gray-500">{formatFileSize(fileSize)}</p>
                    )}
                    <p className="text-sm text-gray-400">Cliquez pour changer de fichier</p>
                  </div>
                ) : (
                  <>
                    <p className="text-gray-600 mb-1">
                      Cliquez pour sélectionner un fichier
                    </p>
                    <p className="text-sm text-gray-400">
                      ou glissez-déposez votre fichier ici
                    </p>
                    {assignment.acceptedFileTypes && (
                      <p className="text-xs text-gray-400 mt-2">
                        Formats acceptés: {assignment.acceptedFileTypes.join(', ')}
                      </p>
                    )}
                  </>
                )}
              </label>
            </div>
          </div>

          {/* Submit Button */}
          <button
            onClick={handleSubmit}
            disabled={isSubmitting || !fileUrl || !submissionTitle.trim()}
            className="w-full flex items-center justify-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-xl hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors font-medium text-lg"
          >
            {isSubmitting ? (
              <Loader2 className="h-5 w-5 animate-spin" />
            ) : (
              <Send className="h-5 w-5" />
            )}
            Soumettre mon travail
          </button>
        </div>
      ) : (
        // Past due, no submission
        <div className="bg-red-50 border-2 border-red-200 rounded-xl p-6 text-center">
          <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-3" />
          <h3 className="text-lg font-semibold text-red-700 mb-2">
            Date limite de soumission dépassée
          </h3>
          <p className="text-red-600">
            Vous n'avez pas soumis votre travail à temps.
          </p>
        </div>
      )}

      {/* Confirmation Modal */}
      {showConfirmModal && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-xl max-w-md w-full p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-3 bg-amber-100 rounded-full">
                <AlertTriangle className="h-6 w-6 text-amber-600" />
              </div>
              <h3 className="text-xl font-bold text-gray-900">Confirmer la soumission</h3>
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-6">
              <p className="text-amber-800 font-medium mb-2">
                Attention: Cette action est irréversible!
              </p>
              <p className="text-amber-700 text-sm">
                Une fois votre devoir soumis, vous ne pourrez plus le modifier ni en soumettre un nouveau.
                Assurez-vous que votre travail est complet et correct avant de confirmer.
              </p>
            </div>

            <div className="space-y-2 mb-6">
              <p className="text-sm text-gray-600">
                <strong>Titre:</strong> {submissionTitle}
              </p>
              <p className="text-sm text-gray-600">
                <strong>Fichier:</strong> {fileName}
              </p>
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => setShowConfirmModal(false)}
                className="flex-1 px-4 py-3 border border-gray-300 text-gray-700 rounded-xl hover:bg-gray-50 transition-colors font-medium"
              >
                Annuler
              </button>
              <button
                onClick={confirmSubmit}
                disabled={isSubmitting}
                className="flex-1 px-4 py-3 bg-blue-600 text-white rounded-xl hover:bg-blue-700 disabled:opacity-50 transition-colors font-medium flex items-center justify-center gap-2"
              >
                {isSubmitting ? (
                  <Loader2 className="h-5 w-5 animate-spin" />
                ) : (
                  <CheckCircle className="h-5 w-5" />
                )}
                Confirmer
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AssignmentsTab;
